import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {error as logError} from 'util/Logging';

export enum DeepResearchToolType {
  // Supervisor-level tools
  ResearchSubAgent = 'ResearchSubAgent',
  Think = 'Think',
  CompleteAnswer = 'CompleteAnswer',
  PublicDataSubAgent = 'PublicDataSubAgent',

  // Sub-agent tools
  SearchTool = 'SearchTool',
  GetWarehouseOverviewTool = 'GetWarehouseOverviewTool',
  SearchNotebookEntries = 'SearchNotebookEntries',
  SearchSequencesTool = 'SearchSequencesTool',
  QueryWarehouseTool = 'QueryWarehouseTool',
  ReadBenchlingDocumentTool = 'ReadBenchlingDocument',
  AttachmentTool = 'AttachmentTool',
  IdsToModelsTool = 'IdsToModelsTool',
  PlottingTool = 'PlottingTool',
  GenerateImageTool = 'GenerateImageTool',
}

export const TOOL_DESCRIPTIONS = {
  [DeepResearchToolType.ResearchSubAgent]: 'Researching',
  [DeepResearchToolType.Think]: 'Thinking',
  [DeepResearchToolType.CompleteAnswer]: 'Writing report',
  [DeepResearchToolType.PublicDataSubAgent]: 'Searching public data',
  [DeepResearchToolType.SearchTool]: 'Searching benchling',
  [DeepResearchToolType.GetWarehouseOverviewTool]: 'Getting warehouse overview',
  [DeepResearchToolType.SearchNotebookEntries]: 'Searching notebook entries',
  [DeepResearchToolType.SearchSequencesTool]: 'Searching sequences',
  [DeepResearchToolType.QueryWarehouseTool]: 'Querying warehouse',
  [DeepResearchToolType.ReadBenchlingDocumentTool]: 'Reading notebook',
  [DeepResearchToolType.AttachmentTool]: 'Reading attachment',
  [DeepResearchToolType.IdsToModelsTool]: 'Looking things up',
  [DeepResearchToolType.PlottingTool]: 'Creating chart',
  [DeepResearchToolType.GenerateImageTool]: 'Generating image',
};

export const TOOL_ICONS: Record<DeepResearchToolType, BenchlingIconName> = {
  [DeepResearchToolType.ResearchSubAgent]: 'magic-wand',
  [DeepResearchToolType.Think]: 'lightbulb',
  [DeepResearchToolType.CompleteAnswer]: 'file',
  [DeepResearchToolType.PublicDataSubAgent]: 'link',
  [DeepResearchToolType.SearchTool]: 'magnifying-glass',
  [DeepResearchToolType.GetWarehouseOverviewTool]: 'warehouse-full',
  [DeepResearchToolType.SearchNotebookEntries]: 'magnifying-glass',
  [DeepResearchToolType.SearchSequencesTool]: 'magnifying-glass',
  [DeepResearchToolType.QueryWarehouseTool]: 'warehouse-full',
  [DeepResearchToolType.ReadBenchlingDocumentTool]: 'file',
  [DeepResearchToolType.AttachmentTool]: 'file',
  [DeepResearchToolType.IdsToModelsTool]: 'magnifying-glass',
  [DeepResearchToolType.PlottingTool]: 'chart-bar',
  [DeepResearchToolType.GenerateImageTool]: 'image',
};

/**
 * MCP (Model Context Protocol) tool constants
 *
 * MCP tools follow the naming convention: "server_name__tool_name"
 * For example: "github__list_repos" represents the "list_repos" tool from the "github" server
 *
 * IMPORTANT: Keep MCP_TOOL_NAME_SEPARATOR in sync with the Python constant:
 * benchling/llm/mcp_client_service/constants.py::SERVER_NAME_SEPARATOR
 */
export const MCP_TOOL_NAME_SEPARATOR = '__';

/** Default icon displayed for all MCP tools */
export const MCP_TOOL_DEFAULT_ICON: BenchlingIconName = 'plug';

/** Default description shown when an MCP tool is executing */
export const MCP_TOOL_DEFAULT_DESCRIPTION = 'Using external tool';

/** Icon displayed for unknown/unsupported tool types */
export const UNKNOWN_TOOL_ICON: BenchlingIconName = 'wrench';

/**
 * Check if a tool name is an MCP tool (contains server name prefix)
 */
export function isMCPTool(toolName: string): boolean {
  return toolName.includes(MCP_TOOL_NAME_SEPARATOR);
}

/**
 * Parse MCP tool name into server name and tool name
 */
export function parseMCPToolName(toolName: string): {serverName: string; originalToolName: string} {
  const separatorIndex = toolName.indexOf(MCP_TOOL_NAME_SEPARATOR);
  if (separatorIndex === -1) {
    return {serverName: '', originalToolName: toolName};
  }
  return {
    serverName: toolName.substring(0, separatorIndex),
    originalToolName: toolName.substring(separatorIndex + MCP_TOOL_NAME_SEPARATOR.length),
  };
}

/**
 * Get the display name for a tool (handles both enum and MCP tools)
 */
export function getToolDisplayName(toolName: DeepResearchToolType | string): string {
  if (isMCPTool(toolName)) {
    const {serverName, originalToolName} = parseMCPToolName(toolName);
    return `${serverName}: ${originalToolName}`;
  } else if (TOOL_DESCRIPTIONS.hasOwnProperty(toolName)) {
    return TOOL_DESCRIPTIONS[toolName as DeepResearchToolType];
  }
  // Log unknown tool names to Sentry for monitoring
  logError(`Unknown tool name: ${toolName}`);
  return toolName;
}

/**
 * Get the icon for a tool (handles both enum and MCP tools)
 */
export function getToolIcon(toolName: DeepResearchToolType | string): BenchlingIconName {
  if (isMCPTool(toolName)) {
    return MCP_TOOL_DEFAULT_ICON;
  }
  return TOOL_ICONS[toolName as DeepResearchToolType] || UNKNOWN_TOOL_ICON;
}

export enum ToolCallStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
}

export interface BaseProgressToolCall {
  // Allow both enum values and strings for MCP tools
  tool: DeepResearchToolType | string;
  toolId: string;
  createdAt: Date;
  status: ToolCallStatus;
  chipName: string;
  responseText?: string;
}

// MCP-specific tool call interface
// TODO (BNCH-140899): include the tool title for user friendly display.
export interface MCPToolCall extends BaseProgressToolCall {
  tool: string; // MCP tool name in format "server_name__tool_name"
  serverName: string;
  originalToolName: string;
  args: Record<string, unknown>;
}
export interface SearchToolCall extends BaseProgressToolCall {
  tool: DeepResearchToolType.SearchTool;
  query: string;
  objectTypes: Array<string>;
}

export interface GetWarehouseOverviewToolCall extends BaseProgressToolCall {
  tool: DeepResearchToolType.GetWarehouseOverviewTool;
}

export interface QueryWarehouseToolCall extends BaseProgressToolCall {
  tool: DeepResearchToolType.QueryWarehouseTool;
  query: string;
}

export interface ReadBenchlingDocumentToolCall extends BaseProgressToolCall {
  tool: DeepResearchToolType.ReadBenchlingDocumentTool;
  documentId: string;
}

export interface AttachmentToolCall extends BaseProgressToolCall {
  tool: DeepResearchToolType.AttachmentTool;
  attachmentId: string;
}

export interface PublicDataSubAgentToolCall extends BaseProgressToolCall {
  tool: DeepResearchToolType.PublicDataSubAgent;
  query: string;
}

export interface SearchNotebookEntriesToolCall extends BaseProgressToolCall {
  tool: DeepResearchToolType.SearchNotebookEntries;
  queries: Array<string>;
  authoredBy?: Array<string>;
  createdAfter?: string;
  createdBefore?: string;
  modifiedAfter?: string;
  modifiedBefore?: string;
  reviewCompletedAfter?: string;
  reviewCompletedBefore?: string;
  reviewStatus?: string;
  reviewRole?: string;
  inProjectOrFolder?: string;
  inStudy?: string;
  offset: number;
}

export interface SearchSequencesToolCall extends BaseProgressToolCall {
  tool: DeepResearchToolType.SearchSequencesTool;
  query: string;
}

export interface IdsToModelsToolCall extends BaseProgressToolCall {
  tool: DeepResearchToolType.IdsToModelsTool;
  objectIds: Array<string>;
  includeRelevantItems: boolean;
}

export interface PlottingToolCall extends BaseProgressToolCall {
  tool: DeepResearchToolType.PlottingTool;
  code: string;
}

export interface GenerateImageToolCall extends BaseProgressToolCall {
  tool: DeepResearchToolType.GenerateImageTool;
  prompt: string;
}

export type ProgressToolCall =
  | SearchToolCall
  | GetWarehouseOverviewToolCall
  | QueryWarehouseToolCall
  | ReadBenchlingDocumentToolCall
  | SearchNotebookEntriesToolCall
  | SearchSequencesToolCall
  | AttachmentToolCall
  | PublicDataSubAgentToolCall
  | IdsToModelsToolCall
  | PlottingToolCall
  | MCPToolCall
  | GenerateImageToolCall
  | BaseProgressToolCall; // We include base for any unsupported tool calls
